'use client';

import { useRef, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import Image from 'next/image';
import Link from 'next/link';
import UIButton from '@/components/atoms/UIButtons/UIButton';
import LoginModal from '@/components/auth/loginModal';
import Modal from '@/components/atoms/Modal';
import Checkbox from '@/components/atoms/Checkbox';
import { useAuth } from '@/providers/userContext';
import { fetchJson } from '@/lib/client/fetchJson';
import { setPostLoginRedirect } from '@/lib/client/postLoginRedirect';
import ImageUpload from '@/assets/images/blank_heart.png';
import UploadPhotoMo from '@/assets/images/blank_heart.png';
import Camera from '@/assets/images/camera.png';
class SentryPhotoUploadError extends Error {
  constructor(message) {
    super(message);
    this.name = 'SentryPhotoUploadError';
  }
}
export default function PhotoUpload({
  heartId
}) {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({
    type: '',
    message: ''
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [fileSizeError, setFileSizeError] = useState('');
  const {
    user
  } = useAuth();
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

  const isDisabled = !selectedFile || !termsAccepted;
  const handleUploadClick = () => {
    if (isUploading) return; // Prevent multiple clicks while uploading

    // Check if user is logged in
    if (!user) {
      setPostLoginRedirect(window.location.href);
      setShowLoginModal(true);
      return;
    }

    // Open the pre-upload modal
    setShowUploadModal(true);
  };
  const handleModalClose = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    setTermsAccepted(false);
    setFileSizeError('');
  };
  const handleFileSelect = event => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setFileSizeError('Photos must be 5mb or less.');
      setSelectedFile(null);
      event.target.value = ''; // Reset input
      return;
    }
    setFileSizeError('');
    setSelectedFile(file);
  };
  const handleSaveUpload = async () => {
    if (!selectedFile || !termsAccepted) return;

    // Close the modal
    setShowUploadModal(false);

    // Reset modal state
    setTermsAccepted(false);
    const fileToUpload = selectedFile;
    setSelectedFile(null);

    // Proceed with upload
    await performUpload(fileToUpload);
  };
  const performUpload = async file_0 => {
    Sentry.logger.info('performUpload started');
    if (!file_0) return;

    // Reset status
    setUploadStatus({
      type: '',
      message: ''
    });
    setUploadProgress(0);

    // Prevent double submission
    setIsUploading(true);
    try {
      // Create FormData to send the file
      const formData = new FormData();
      formData.append('file', file_0);
      formData.append('userId', user.id); // Include user ID

      Sentry.logger.info(`file: ${file_0.name}; ${file_0.type}; ${file_0.size}`);
      Sentry.logger.info('userId: ' + user.id);

      // Upload to API with progress tracking
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', e => {
        if (e.lengthComputable) {
          const percentComplete = Math.round(e.loaded / e.total * 100);
          setUploadProgress(percentComplete);
          Sentry.logger.info('percent complete: ' + percentComplete);
        }
      });

      // Handle completion
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e_0) {
              reject({
                error: 'Invalid response from server'
              });
              Sentry.logger.info('Failed with invalid response:');
              Sentry.logger.info(JSON.stringify(e_0));
              Sentry.logger.info(JSON.stringify(xhr));
              throw new SentryPhotoUploadError('Invalid response from server');
            }
          } else {
            try {
              reject(JSON.parse(xhr.responseText));
            } catch (e_1) {
              reject({
                error: `Upload failed with status ${xhr.status}`
              });
              Sentry.logger.info('Failed with response:');
              Sentry.logger.info(JSON.stringify(e_1));
              Sentry.logger.info(JSON.stringify(xhr));
              throw new SentryPhotoUploadError(`Upload failed with status ${xhr.status}`);
            }
          }
        });
        xhr.addEventListener('error', e_2 => {
          reject({
            error: 'Network error'
          });
          Sentry.logger.info('Failed with network error:');
          Sentry.logger.info(JSON.stringify(e_2));
          Sentry.logger.info(JSON.stringify(xhr));
          throw new SentryPhotoUploadError('Network error');
        });
        xhr.addEventListener('abort', () => reject({
          error: 'Upload cancelled'
        }));
      });
      xhr.open('POST', '/api/hearts/upload-photo');
      xhr.send(formData);
      const result = await uploadPromise;
      //console.log('Upload result:', result);

      if (result.success && result.url && result.filename) {
        // throw new SentryPhotoUploadError('test error');
        //console.log('result:', result);
        // Register the photo with the visitor API
        try {
          var result2 = await fetchJson('/api/visitor/upload-photo', {
            method: 'POST',
            body: {
              heartId: heartId,
              url: result.url,
              fileName: file_0.name,
              path: result.filename
            }
          });
          if (result2.success) {
            //console.log('update result', result2);
            setUploadStatus({
              type: 'success',
              message: 'Photo uploaded successfully!'
            });
            setUploadProgress(100);

            // Auto-clear success message after 5 seconds
            setTimeout(() => {
              setUploadStatus({
                type: '',
                message: ''
              });
              setUploadProgress(0);
            }, 5000);
          } else {
            console.error('Failed to register photo with visitor API: Server error. ', result2.data);
            setUploadStatus({
              type: 'error',
              message: 'Failed to save photo record. An unexpected error occured.'
            });
            setUploadProgress(0);
          }
        } catch (apiError) {
          console.error('Failed to register photo with visitor API:', apiError);
          setUploadStatus({
            type: 'error',
            message: apiError.body?.message || apiError.message || 'Failed to save photo record'
          });
          setUploadProgress(0);
        }
      } else {
        // Handle error response
        const errorMessage_0 = result.error || 'Failed to upload photo';
        console.error('Photo upload failed:', result);
        setUploadStatus({
          type: 'error',
          message: errorMessage_0
        });
        setUploadProgress(0);

        // Clear error message after a delay for rate limit errors
        if (result.retryAfter) {
          setTimeout(() => {
            setUploadStatus({
              type: '',
              message: ''
            });
          }, result.retryAfter * 1000);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);

      // Handle rate limiting (429) and other errors
      let errorMessage = 'An error occurred while uploading the photo';
      if (error.error) {
        errorMessage = error.error;
      }
      setUploadStatus({
        type: 'error',
        message: errorMessage
      });
      setUploadProgress(0);
      Sentry.logger.info(JSON.stringify(error));
      throw new SentryPhotoUploadError('Throwing error on photo upload error');
    } finally {
      setIsUploading(false);
    }
  };
  return <>
      <section className="2xl:py-10 xl:py-7 lg:py-4 py-2" id="uploadPhotos">
        <div className="container-wrap">
          <div className="2xl:max-w-[1060px] xl:max-w-[900px] lg:max-w-[800] md:max-w-600 md:mx-auto -mx-4 flex items-center justify-between relative md:rounded-[0] rounded-[14px] aspect-[375/556] md:aspect-auto  print:aspect-auto">
            <div className="absolute inset-0 md:relative print:relative print:inset-auto">
              <div className="block md:hidden absolute inset-0 rounded-t-[14px] bg-[linear-gradient(0deg,rgba(0,0,0,0)_52.7%,rgba(0,0,0,0.4)_100%)] " />
              <Image src={ImageUpload} width="533" height="706" alt="Woman taking a selfie in front of a colorful heart mural inside a phone frame." className="md:block hidden" />
              <Image src={UploadPhotoMo} width="375" height="556" alt="Smiling woman taking a selfie in front of a painted heart sculpture." className="rounded-[14px] block md:hidden w-full h-auto object-cover" />
            </div>
            <div className="flex flex-col items-center md:justify-center pt-7 pb-4 justify-between text-center max-w-[500px] mx-auto relative z-10 md:h-auto h-full">
              <h2 className="mb-9 max-w-[70%] md:max-w-full md:text-[#323232] text-white print:text-black text-center 2xl:text-[2.7em] xl:text-5xl lg:text-4xl md:text-3xl text-[1.3em] font-semibold leading-[1em]">
                Share your photos of your hearts
              </h2>

              {/* Hidden file input */}
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/gif" onChange={handleFileSelect} className="hidden" aria-label="Upload photo file (JPEG, PNG, WEBP, or GIF, maximum 5MB)" id="hidden-photo-upload" />

              <div className="md:w-full w-[78px]">
                <UIButton variant="gradient" className="justify-center cursor-pointer flex-wrap lg:!min-w-[182px] md:!min-w-40 !min-w-[78px] backdrop-blur-[10px] md:backdrop-blur-[0px] mx-auto md:!bg-white/50 !bg-black/50 md:!text-[#323232] !text-white" onClick={handleUploadClick} disabled={isUploading}>
                  <Image src={Camera} width="24" height="22" alt="Camera" className="md:hidden mb-2 pt-3" />{' '}
                  <span className="md:pb-0 pb-3 md:max-w-max max-w-[70px]">
                    {isUploading ? `${uploadProgress}%` : 'Upload Photos'}
                  </span>
                </UIButton>
              </div>

              {/* Upload progress bar */}
              {isUploading && uploadProgress > 0 && <div className="mt-4 w-full max-w-xs">
                  <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-full transition-all duration-300" style={{
                  width: `${uploadProgress}%`
                }} />
                  </div>
                </div>}

              {/* Upload status message */}
              {uploadStatus.message && <div className={`mt-4 px-4 py-2 rounded-md text-sm ${uploadStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {uploadStatus.message}
                </div>}
            </div>
          </div>
        </div>
      </section>

      {/* Login Modal */}
      {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}

      {/* Pre-Upload Photo Modal */}
      {showUploadModal && <Modal isOpen={true} onClose={handleModalClose} ModalBodyClass="pb-4">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl lg:text-2xl xl:text-[26px] text-center font-semibold text-[#323232] mb-3">
                Upload Photo
              </h2>
            </div>

            <div className="space-y-4">
              {/* File Input */}
              <div>
                <label htmlFor="photo-file-input" className="block w-full px-4 py-3 text-center border-2 border-dashed border-[#DDDDDD] rounded-md cursor-pointer hover:border-[#00EAFD] transition-colors">
                  <span className="text-[#626262] text-sm">
                    {selectedFile ? selectedFile.name : 'Click to select a photo'}
                  </span>
                  <input id="photo-file-input" type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/gif" onChange={handleFileSelect} className="hidden" aria-describedby="photo-file-instructions" aria-invalid={fileSizeError ? 'true' : 'false'} aria-errormessage={fileSizeError ? 'photo-file-error' : undefined} />
                </label>
                <p id="photo-file-instructions" className="sr-only">
                  Select a photo file in JPEG, PNG, WEBP, or GIF format. Maximum file size is 5
                  megabytes.
                </p>
              </div>

              {/* File Size Error */}
              {fileSizeError && <div id="photo-file-error" role="alert" aria-live="assertive" className="bg-red-100 text-red-800 px-4 py-2 rounded-md text-sm flex items-center justify-between">
                  <span>{fileSizeError}</span>
                  <button onClick={() => setFileSizeError('')} className="ml-2 text-red-800 hover:text-red-900 font-bold" aria-label="Dismiss file size error message">
                    ×
                  </button>
                </div>}

              {/* Terms Checkbox */}
              <div className="pt-2">
                <Checkbox options={[{
              label: <span className="text-[#626262] text-sm leading-[1.5] font-normal">
                          By uploading, I confirm this photo is mine to share and follows the{' '}
                          <Link href="/terms-of-use" className="text-[#626262] underline" target="_blank" rel="noopener noreferrer">
                            Terms of Use
                          </Link>{' '}
                          and{' '}
                          <Link href="/privacy-policy" className="text-[#626262] underline" target="_blank" rel="noopener noreferrer">
                            Privacy Policy
                          </Link>
                          .
                        </span>,
              value: 'termsAccepted'
            }]} className="items-start" checkBoxClass="mt-1" checkedColor="bg-[#00EAFD]" checkColor="text-[#323232]" name="termsAccepted" selectedValues={termsAccepted ? ['termsAccepted'] : []} onChange={vals => setTermsAccepted(vals.includes('termsAccepted'))} />
              </div>

              {/* Buttons */}
              <div className="space-y-3 pt-4">
                <UIButton type="button" rounded="full" className={`mx-auto max-w-[343px] w-full  !mb-3 !block !px-8 !py-3 mt-1 !text-base ${isDisabled ? 'cursor-not-allowed hover:bg-black/30 hover:text-white' : ''}`} variant={isDisabled ? 'default' : 'primaryTheme'} disabled={isDisabled} onClick={handleSaveUpload}>
                  Save
                </UIButton>

                {/* Micro-copy under Save button */}
                <p className="text-xs text-[#626262] text-center leading-[1.4] px-4">
                  Photos will be published to this page pending review and approval by the Parade of
                  Hearts team.
                </p>

                <UIButton type="button" rounded="full" className="mx-auto max-w-[343px] w-full block" variant="outline" onClick={handleModalClose}>
                  Cancel
                </UIButton>
              </div>
            </div>
          </div>
        </Modal>}
    </>;
}